/* eslint-disable no-undef */
import React, {
  useState,
  useLayoutEffect,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Labelbox,
  FrameBox,
  Table,
  Button,
  Modal,
  EditTimesheetModal,
} from "../../components";
import {
  AddCircleOutlineOutlined,
  MoreVert,
  GetApp,
  PictureAsPdf,
  TableChart,
} from "@material-ui/icons";
import { IconButton, Menu, MenuItem } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@material-ui/core";
import "./timesheet.scss";
import {
  TIMESHEET_ACTION,
  EMPLOYEE_ACTION,
  MASTER_ACTION,
} from "../../redux/action";
import {
  dateFormatter,
  dateTimeFormatter,
  dropdownName,
  Toaster,
  getRolePath,
} from "../../utils/common";
import {
  compareDateOnly,
  formatDateOnlyDisplay,
  formatDateOnlyYmd,
  isDateOnlyInRange,
  parseYmdToTimestamp,
} from "../../utils/dateOnly";
import { checkValidation, onSubmitValidation } from "../../utils/common";
import {
  formatMinutesAsHMM,
  normalizeActivityOption,
  parseItemHoursToMinutes,
  classifyTimesheetItemMinutes,
  isTimesheetTotalBelowMinimum,
  timesheetMinimumHoursForHolidays,
} from "../../utils/timesheetHelpers";
import {
  getReportDateRange,
  REPORT_DATE_RANGE_OPTIONS,
  timesheetOverlapsDateRange,
} from "../../utils/reportDateRanges";
import { useNavigate, useLocation } from "react-router-dom";
import TimeSheetView from "../../components/timesheetView";
import * as FileSaver from "file-saver";
import { utils, write } from "xlsx";
import { jsPDF } from "jspdf";
import {
  canViewAllTimesheets,
  isTimesheetAdminViewEmployee,
} from "../../utils/timesheetAccess";
import {
  TIMESHEET_IMPORT_HEADERS,
  mapTimesheetItemToImportRow,
  formatClientForImportRow,
} from "../../utils/timesheetExcelTemplate";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [viewMode, setViewMode] = useState({ state: false, data: {} });
  const [subActivityState, setSubActivityState] = useState({});
  const [showManagerOwnTimesheets, setShowManagerOwnTimesheets] =
    useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTimesheetId, setEditTimesheetId] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportButtonRef = useRef(null);
  const [deleteTimesheetDialog, setDeleteTimesheetDialog] = useState({
    open: false,
    timesheetData: null,
  });
  const [dateRangePreset, setDateRangePreset] = useState("All");

  const userRole = localStorage.getItem("user_role");
  const isTimesheetPowerUser = isTimesheetAdminViewEmployee();
  const isEmployeeRole = userRole === "employee";
  const isAdmin = userRole === "admin" || isTimesheetPowerUser;
  const isManager = userRole === "manager" && !isTimesheetPowerUser;
  const isEmployee = userRole === "employee";
  const isTimesheetAdminViewer = canViewAllTimesheets();
  const isTimesheetPowerEmployee = isTimesheetPowerUser;
  const isRegularEmployee = isEmployee && !isTimesheetPowerEmployee;

  const defaultValues = {
    start_date: {
      // value: dateFormatter(false, true),
      value: "",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    end_date: {
      // value: dateFormatter(false, true),
      value: "",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    employee_id: {
      value:
        isEmployeeRole && !isTimesheetPowerEmployee
          ? localStorage.getItem("user_id")
          : "",
      validation:
        isEmployeeRole && !isTimesheetPowerEmployee
          ? [{ name: "required" }]
          : [],
      error: null,
      errmsg: null,
    },
    rejected_comments: {
      value: "",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
  };
  const header = [
    { id: "start_date", label: "Start Date" },
    { id: "end_date", label: "End Date" },
    { id: "no_of_hours", label: "Total Hours" },
    { id: "status", label: "Status" },
    { id: "updated", label: "Last Update" },
    { id: "action", label: "Action" },
  ];
  const header1 = [
    { id: "start_date", label: "Start Date" },
    { id: "end_date", label: "End Date" },
    { id: "employee_id", label: "Employee" },
    { id: "no_of_hours", label: "Total Hours" },
    { id: "status", label: "Status" },
    { id: "updated", label: "Last Update" },
    { id: "action", label: "Action" },
  ];
  const [fieldConfiguration, setFieldConfiguration] =
    React.useState(defaultValues);
  const [openModal, setOpenModal] = useState(false);
  const rejectedEmployeeId = useRef();

  useLayoutEffect(() => {
    dispatch(EMPLOYEE_ACTION.getActiveEmployees());
    dispatch(TIMESHEET_ACTION.getAllTimesheets());
  }, []);

  const {
    timesheet: { allTimesheets },
    employee: { activeEmployees },
    master: { activity: activityMaster = [] },
  } = useSelector((state) => state);

  // Handle navigation from notifications
  useEffect(() => {
    if (location.state?.fromNotification && location.state?.timesheet_id) {
      const timesheetId = location.state.timesheet_id;
      // Wait for timesheets to load, then open the specific timesheet
      if (allTimesheets && allTimesheets.length > 0) {
        // Check if the timesheet exists in the loaded timesheets
        const timesheetExists = allTimesheets.some(
          (ts) =>
            ts.timesheet_id === timesheetId ||
            ts._rawData?.timesheet_id === timesheetId
        );
        if (timesheetExists) {
          handleViewTimesheetInternal(timesheetId);
        } else {
          Toaster.warning("Timesheet not found");
        }
        // Clear the state to prevent reopening on re-render
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, allTimesheets, navigate]);

  // Internal handlers that work with timesheet_id directly
  const handleViewTimesheetInternal = async (timesheet_id) => {
    const timesheetEntries = await TIMESHEET_ACTION.getTimesheetEntries({
      timesheet_id,
    });
    if (timesheetEntries && timesheetEntries.length > 0) {
      const DATA = timesheetEntries[0];
      setViewMode({ state: true, data: DATA });
    }
  };

  const handleEditTimesheetInternal = (timesheetData) => {
    const raw = timesheetData._rawData;
    const timesheetId = raw?.timesheet_id;

    if (timesheetId) {
      // ✅ RESTORE START & END DATE INTO STATE
      setFieldConfiguration((prev) => ({
        ...prev,
        start_date: {
          ...prev.start_date,
          value: raw.start_date,
          minDate: raw.start_date,
          maxDate: raw.end_date,
        },
        end_date: {
          ...prev.end_date,
          value: raw.end_date,
          minDate: raw.start_date,
          maxDate: raw.end_date,
        },
      }));

      setEditTimesheetId(timesheetId);
      setEditModalOpen(true);
    }
  };

  const handleSubmitTimesheetInternal = async (timesheetData) => {
    try {
      const timesheetEntries = await TIMESHEET_ACTION.getTimesheetEntries({
        timesheet_id: timesheetData.timesheet_id,
      });
      if (timesheetEntries && timesheetEntries.length > 0) {
        const data = timesheetEntries[0];
        const holidays = await TIMESHEET_ACTION.getTimesheetHolidays({
          start_date: formatDateOnlyYmd(data.start_date),
          end_date: formatDateOnlyYmd(data.end_date),
          employee_id: data.employee_id,
        });
        const holidayDates = new Set(
          (holidays || []).map((holiday) => holiday.date),
        );
        const holidayEntry = (data.itemDetails || []).find((item) =>
          holidayDates.has(formatDateOnlyYmd(item?.date)),
        );
        if (holidayEntry) {
          Toaster.error(
            "Timesheet entries cannot be added on a holiday",
          );
          return;
        }
        const requiredHours = timesheetMinimumHoursForHolidays(
          holidayDates.size,
        );
        if (isTimesheetTotalBelowMinimum(data.total_hours, requiredHours)) {
          Toaster.error(
            `Total hours must be at least ${requiredHours} hours to submit the timesheet`,
          );
          return;
        }
        const response = await TIMESHEET_ACTION.insertUpdateTimesheet({
          params: {
            ...data,
            status: 1, // Submit status
            employee_id: data.employee_id,
            itemDetails: data.itemDetails,
          },
          insertUpdateFlag: 1,
          timesheet_id: timesheetData.timesheet_id,
        });
        if (response.status === 200 && response.data.status === 1) {
          Toaster.success("Timesheet submitted successfully");
          runTimesheetSearch();
        } else {
          Toaster.error("Error submitting timesheet");
        }
      }
    } catch (error) {
      Toaster.error("Error submitting timesheet");
    }
  };

  const handleDeleteTimesheetInternal = (timesheetData) => {
    setDeleteTimesheetDialog({
      open: true,
      timesheetData,
    });
  };
  const confirmDeleteTimesheet = async () => {
    const timesheetData = deleteTimesheetDialog.timesheetData;
    if (!timesheetData) return;

    try {
      const result = await TIMESHEET_ACTION.deleteTimesheet(
        timesheetData.timesheet_id
      );

      if (result.status) {
        Toaster.success(result.message);
        runTimesheetSearch();
      } else {
        Toaster.error(result.message || "Error deleting timesheet");
      }
    } catch (error) {
      console.error("Error deleting timesheet:", error);
      Toaster.error("Error deleting timesheet");
    } finally {
      setDeleteTimesheetDialog({ open: false, timesheetData: null });
    }
  };

  const handleApproveTimesheetInternal = async (timesheet_id) => {
    const signal = await TIMESHEET_ACTION.approveTimesheet(timesheet_id);
    if (signal.status) {
      Toaster.success(signal.message);
      runTimesheetSearch();
    } else {
      Toaster.error(signal.message || "Timesheet not approved successfully");
    }
  };

  const handleRejectTimesheetInternal = (timesheet_id) => {
    setOpenModal(true);
    rejectedEmployeeId.current = timesheet_id;
  };

  // ActionMenu Component for timesheet actions
  const ActionMenu = ({ timesheetData, handlers }) => {
    const [localAnchorEl, setLocalAnchorEl] = useState(null);

    const handleMenuClick = (event) => {
      event.stopPropagation();
      setLocalAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setLocalAnchorEl(null);
    };

    const hasAnyAction =
      timesheetData.enabledActions &&
      Object.values(timesheetData.enabledActions).some((v) => v === true);
    if (!hasAnyAction) {
      return <span>---</span>;
    }

    return (
      <>
        <IconButton
          size="small"
          onClick={handleMenuClick}
          style={{ padding: "4px" }}
        >
          <MoreVert />
        </IconButton>
        <Menu
          anchorEl={localAnchorEl}
          open={Boolean(localAnchorEl)}
          onClose={handleMenuClose}
        >
          {timesheetData.enabledActions.edit && (
            <MenuItem
              onClick={() => {
                handlers.onEdit(timesheetData);
                handleMenuClose();
              }}
            >
              Edit
            </MenuItem>
          )}
          {timesheetData.enabledActions.submit && (
            <MenuItem
              onClick={async () => {
                await handlers.onSubmit(timesheetData);
                handleMenuClose();
              }}
            >
              Submit
            </MenuItem>
          )}
          {timesheetData.enabledActions.delete && (
            <MenuItem
              onClick={async () => {
                await handlers.onDelete(timesheetData);
                handleMenuClose();
              }}
            >
              Delete
            </MenuItem>
          )}
          {timesheetData.enabledActions.approve && (
            <MenuItem
              onClick={async () => {
                await handlers.onApprove(timesheetData.timesheet_id);
                handleMenuClose();
              }}
            >
              Approve
            </MenuItem>
          )}
          {timesheetData.enabledActions.reject && (
            <MenuItem
              onClick={() => {
                handlers.onReject(timesheetData.timesheet_id);
                handleMenuClose();
              }}
            >
              Reject
            </MenuItem>
          )}
          {timesheetData.enabledActions.view && (
            <MenuItem
              onClick={async () => {
                await handlers.onView(timesheetData.timesheet_id);
                handleMenuClose();
              }}
            >
              View
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const managerTeamEmployees = useMemo(() => {
    if (!isManager) return [];
    const userId = String(localStorage.getItem("user_id") || "");
    return (activeEmployees || []).filter(
      (emp) =>
        String(emp?.manager_id || "") === userId &&
        String(emp?.employee_id || "") !== userId,
    );
  }, [activeEmployees, isManager]);

  const timesheetTableData = useMemo(() => {
    const userId = localStorage.getItem("user_id");

    let RESPONSE = [...allTimesheets];

    const filterStart = fieldConfiguration.start_date.value;
    const filterEnd = fieldConfiguration.end_date.value;
    if (filterStart || filterEnd) {
      RESPONSE = RESPONSE.filter((ts) =>
        timesheetOverlapsDateRange(ts, filterStart, filterEnd),
      );
    }

    // Filter by selected employee (admin / manager team view). Skip when manager/admin
    // toggles "Show My Timesheets" — own timesheets take priority over dropdown.
    if (
      isTimesheetAdminViewer &&
      fieldConfiguration.employee_id.value &&
      !((isManager || isAdmin) && showManagerOwnTimesheets)
    ) {
      RESPONSE = RESPONSE.filter(
        (ts) =>
          String(ts.employee_id) ===
          String(fieldConfiguration.employee_id.value),
      );
    }

    // Filter based on checkbox state for managers and admins
    if (isManager || isAdmin) {
      if (showManagerOwnTimesheets) {
        // Show only manager's/admin's own timesheets
        RESPONSE = RESPONSE.filter((ts) => String(ts.employee_id) === String(userId));
      } else if (isManager) {
        const managerEmployeeIds = managerTeamEmployees.map(
          (emp) => emp.employee_id,
        );

        RESPONSE = RESPONSE.filter(
          (ts) =>
            managerEmployeeIds.some(
              (id) => String(id) === String(ts.employee_id),
            ) && String(ts.employee_id) !== String(userId),
        );
      }
    }

    const sortedData = RESPONSE.sort((a, b) =>
      a.updatedAt < b.updatedAt ? 1 : b.updatedAt < a.updatedAt ? -1 : 0
    );

    return sortedData.map((data) => {
      const {
        start_date,
        end_date,
        employee_name,
        employee_id,
        total_hours,
        status,
        timesheet_id,
        updatedAt,
      } = data;

      const isOwner = String(employee_id) === String(userId);
      const canEdit = isOwner && status !== 2; // Can't edit if approved

      // Determine available actions based on role and ownership
      let enabledActions = {};
      if (isOwner && (isEmployee || isManager)) {
        // Owner actions: Edit, Submit, Delete (if not approved), View
        enabledActions = {
          edit: canEdit,
          submit: canEdit && status !== 1, // Can submit if not already pending
          delete: canEdit,
          view: true,
        };
      } else if ((isManager || isAdmin) && !isOwner) {
        // Manager/Admin actions for others: Approve, Reject, View
        enabledActions = {
          approve: status === 1, // Can approve if pending
          reject: status === 1, // Can reject if pending
          view: true,
        };
      } else if ((isManager || isAdmin) && isOwner) {
        // Manager/Admin viewing their own: Edit, Submit, Delete, View
        enabledActions = {
          edit: canEdit,
          submit: canEdit && status !== 1,
          delete: canEdit,
          view: true,
        };
      }

      const timesheetRowData = {
        timesheet_id,
        employee_id_original: employee_id,
        status_code: status,
        _rawData: data,
        enabledActions,
      };

      return {
        start_date: dateFormatter(start_date),
        end_date: dateFormatter(end_date),
        employee_id: employee_name, // Map employee_name to employee_id for table display
        employee_name, // Keep for hiddenFields check
        employee_id_original: employee_id, // Keep original employee_id for filtering
        no_of_hours: total_hours, // Map to match header id
        total_hours, // Keep for reference
        status:
          status === 0
            ? "Saved"
            : status === 1
            ? "Pending"
            : status === 2
            ? "Approved"
            : "Rejected",
        status_code: status, // Keep status code for logic
        updated: dateTimeFormatter(updatedAt), // Map to match header id
        updatedAt: dateTimeFormatter(updatedAt), // Keep for reference
        timesheet_id,
        enabledActions,
        _rawData: data, // Keep raw data for actions
        action: (
          <ActionMenu
            timesheetData={timesheetRowData}
            handlers={{
              onEdit: handleEditTimesheetInternal,
              onSubmit: handleSubmitTimesheetInternal,
              onDelete: handleDeleteTimesheetInternal,
              onApprove: handleApproveTimesheetInternal,
              onReject: handleRejectTimesheetInternal,
              onView: handleViewTimesheetInternal,
            }}
          />
        ),
      };
    });
  }, [
    allTimesheets,
    showManagerOwnTimesheets,
    isManager,
    isAdmin,
    isEmployee,
    isTimesheetPowerEmployee,
    managerTeamEmployees,
    fieldConfiguration.employee_id.value,
    fieldConfiguration.start_date.value,
    fieldConfiguration.end_date.value,
  ]);

  const employeesData = useMemo(() => {
    const roster =
      isManager ? managerTeamEmployees : activeEmployees || [];

    const options = roster.map((data) => {
      const { employee_id } = data;
      return {
        id: employee_id,
        value: dropdownName(data),
      };
    });

    if (userRole === "employee" && !isTimesheetPowerEmployee) return options;

    return [{ id: "", value: "All Employees", keepAtTop: true }, ...options];
  }, [
    activeEmployees,
    isManager,
    managerTeamEmployees,
    userRole,
    isTimesheetPowerEmployee,
  ]);

  function handleCancel() {
    setDateRangePreset("All");
    setFieldConfiguration(defaultValues);
    dispatch(TIMESHEET_ACTION.getAllTimesheets());
  }

  const handleViewTimesheetForRow = async (row) => {
    const timesheet_id = row.timesheet_id;
    if (timesheet_id) {
      await handleViewTimesheetInternal(timesheet_id);
    }
  };

  const onAction = async (action, actionData, actionIndex) => {
    // Legacy handler for old action format (not used with new ActionMenu, but kept for compatibility)
    if (typeof action === "object" && action.text) {
      if (action.text === "Approve") {
        await handleApproveTimesheetInternal(actionData.timesheet_id);
      } else if (action.text === "Reject") {
        handleRejectTimesheetInternal(actionData.timesheet_id);
      } else if (action.text === "View") {
        await handleViewTimesheetInternal(actionData.timesheet_id);
      } else if (action.text === "Edit") {
        handleEditTimesheetInternal(actionData);
      }
    }
  };

  const applyDateFields = (start_date, end_date) => {
    setFieldConfiguration((prev) => ({
      ...prev,
      start_date: {
        ...prev.start_date,
        value: start_date || "",
        error: null,
        errmsg: null,
      },
      end_date: {
        ...prev.end_date,
        value: end_date || "",
        error: null,
        errmsg: null,
      },
    }));
  };

  const runTimesheetSearch = useCallback(
    (options = {}) => {
      const userId = localStorage.getItem("user_id");
      const preset = options.preset ?? dateRangePreset;
      const start =
        options.start_date !== undefined
          ? options.start_date
          : fieldConfiguration.start_date.value;
      const end =
        options.end_date !== undefined
          ? options.end_date
          : fieldConfiguration.end_date.value;
      const employeeId =
        options.employee_id !== undefined
          ? options.employee_id
          : fieldConfiguration.employee_id.value;

      if (preset !== "All" && (!start || !end)) {
        Toaster.warning("Please select start and end dates.");
        return;
      }

      const searchParams = {};
      if (start) searchParams.start_date = start;
      if (end) searchParams.end_date = end;

      if ((isManager || isAdmin) && showManagerOwnTimesheets) {
        searchParams.employee_id = userId;
        searchParams.include_saved = true;
      } else if (employeeId) {
        searchParams.employee_id = employeeId;
      }

      dispatch(TIMESHEET_ACTION.getAllTimesheets(searchParams));
    },
    [
      dateRangePreset,
      dispatch,
      fieldConfiguration.start_date.value,
      fieldConfiguration.end_date.value,
      fieldConfiguration.employee_id.value,
      isManager,
      isAdmin,
      showManagerOwnTimesheets,
    ],
  );

  const handleDateRangePresetChange = (preset) => {
    const { start_date, end_date } = getReportDateRange(preset);
    setDateRangePreset(preset);
    applyDateFields(start_date, end_date);
    runTimesheetSearch({
      preset,
      start_date: start_date || "",
      end_date: end_date || "",
    });
  };

  const onChange = (value, key) => {
    if (key === "start_date" || key === "end_date") {
      setDateRangePreset("Custom");
    }

    checkValidation({
      key,
      value,
      state: fieldConfiguration,
      setState: setFieldConfiguration,
    });

    if (key === "employee_id" && isTimesheetAdminViewer) {
      runTimesheetSearch({ employee_id: value });
    }
  };

  const onSearch = () => {
    runTimesheetSearch();
  };

  // Re-fetch when manager/admin toggles own vs team timesheets
  useEffect(() => {
    if (!isManager && !isAdmin) return;
    if (dateRangePreset !== "All") {
      if (
        !fieldConfiguration.start_date.value ||
        !fieldConfiguration.end_date.value
      ) {
        return;
      }
    }
    runTimesheetSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when checkbox toggles
  }, [showManagerOwnTimesheets]);

  // Handle click outside to close export menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuAnchor &&
        exportButtonRef.current &&
        !exportButtonRef.current.contains(event.target) &&
        !event.target.closest('[role="menu"]')
      ) {
        setExportMenuAnchor(null);
      }
    };

    if (exportMenuAnchor) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [exportMenuAnchor]);

  const onRejectSubmit = async () => {
    const validate = onSubmitValidation({
      state: fieldConfiguration,
      setState: setFieldConfiguration,
      checkFields: ["rejected_comments"],
    });
    if (!validate.length) {
      const signal = await TIMESHEET_ACTION.rejectTimesheet({
        timesheet_id: rejectedEmployeeId.current,
        comments: fieldConfiguration.rejected_comments.value,
      });
      if (signal.status) {
        runTimesheetSearch();
        Toaster.success(signal.message);
      } else Toaster.error(signal.message || "Timesheet has not rejected");
      rejectedEmployeeId.current = null;
      fieldConfiguration.rejected_comments.value = "";
      setOpenModal(false);
    }
  };

  const getTimesheetStatusCode = (status) => {
    const code = Number(status);
    return Number.isNaN(code) ? null : code;
  };

  const getTimesheetStatusLabel = (status) => {
    const code = getTimesheetStatusCode(status);
    if (code === 0) return "Saved";
    if (code === 1) return "Pending";
    if (code === 2) return "Approved";
    return "Rejected";
  };

  // Approved + pending timesheets in current search results (for export)
  const getExportableTimesheets = useMemo(() => {
    const exportable = timesheetTableData.filter(
      (ts) => ts.status_code === 1 || ts.status_code === 2,
    );
    return exportable.sort((a, b) => {
      const dateA = parseYmdToTimestamp(
        a._rawData?.start_date || a.start_date,
      );
      const dateB = parseYmdToTimestamp(
        b._rawData?.start_date || b.start_date,
      );
      return (dateA ?? 0) - (dateB ?? 0);
    });
  }, [timesheetTableData]);

  const canExportTimesheets =
    (isAdmin || isManager) && getExportableTimesheets.length > 0;

  const canExportEmployeeImportExcel =
    isRegularEmployee && timesheetTableData.length > 0;

  // Export must respect filter dates (e.g. This Month), even when a week
  // spans the previous month and those early days appear in the list.
  const isExportItemInFilterRange = useCallback(
    (itemDate) => {
      const filterStart = fieldConfiguration.start_date.value;
      const filterEnd = fieldConfiguration.end_date.value;
      if (!filterStart && !filterEnd) return true;
      return isDateOnlyInRange(itemDate, filterStart, filterEnd);
    },
    [fieldConfiguration.start_date.value, fieldConfiguration.end_date.value],
  );

  const sortByDateAsc = (a, b) => {
    const dateA = new Date(a.Date || a.date);
    const dateB = new Date(b.Date || b.date);
    return dateA - dateB; // older → newer
  };

  // Employee export — same columns/layout as Excel import template
  const handleExportEmployeeImportExcel = async () => {
    if (!timesheetTableData.length) {
      Toaster.warning("No timesheets to export");
      return;
    }

    let exportRows = [];

    for (const ts of timesheetTableData) {
      const timesheetId = ts._rawData?.timesheet_id;
      if (!timesheetId) continue;

      const res = await TIMESHEET_ACTION.getTimesheetEntries({
        timesheet_id: timesheetId,
      });

      if (!res || !res.length) continue;

      const timesheet = res[0];
      if (!timesheet.itemDetails || !timesheet.itemDetails.length) continue;

      timesheet.itemDetails.forEach((item) => {
        if (!isExportItemInFilterRange(item.date)) return;
        exportRows.push(mapTimesheetItemToImportRow(item));
      });
    }

    if (!exportRows.length) {
      Toaster.warning("No timesheet items to export");
      return;
    }

    exportRows.sort((a, b) => compareDateOnly(a._sortYmd, b._sortYmd));
    const dataRows = exportRows.map(({ _sortYmd, ...row }) => row);

    const ws = utils.aoa_to_sheet([TIMESHEET_IMPORT_HEADERS]);
    utils.sheet_add_json(ws, dataRows, {
      origin: "A2",
      skipHeader: true,
    });

    const wb = {
      Sheets: { "Timesheet template": ws },
      SheetNames: ["Timesheet template"],
    };

    const excelBuffer = write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    const employeeName = String(
      localStorage.getItem("first_name") || "Employee",
    ).replace(/[^a-zA-Z0-9]/g, "_");

    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Timesheet_${employeeName}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    Toaster.success("Timesheet exported for Excel import");
  };

  // Export to Excel
  const handleExportExcel = async () => {
    if (getExportableTimesheets.length === 0) {
      Toaster.warning("No approved or pending timesheets to export");
      return;
    }

    let exportData = [];
    let totalApprovedMinutes = 0;
    let totalPendingMinutes = 0;
    let totalBillableMinutes = 0;
    let totalNonBillableMinutes = 0;
    let totalIdleMinutes = 0;
    let totalLeaveMinutes = 0;

    for (const ts of getExportableTimesheets) {
      const res = await TIMESHEET_ACTION.getTimesheetEntries({
        timesheet_id: ts._rawData.timesheet_id,
      });

      if (!res || !res.length) continue;

      const timesheet = res[0];

      if (!timesheet.itemDetails || !timesheet.itemDetails.length) {
        continue;
      }
      const statusCode = getTimesheetStatusCode(timesheet.status);

      timesheet.itemDetails.forEach((item) => {
        if (!isExportItemInFilterRange(item.date)) return;

        const itemMinutes = parseItemHoursToMinutes(item.hours);
        const isLeaveItem =
          String(item?.billed_type || item?.billing_type || item?.billingType || "")
            .trim()
            .toLowerCase()
            .replace(/[-\s_]/g, "") === "leave";

        if (isLeaveItem) {
          totalLeaveMinutes += itemMinutes;
        } else {
          if (statusCode === 2) {
            totalApprovedMinutes += itemMinutes;
          } else if (statusCode === 1) {
            totalPendingMinutes += itemMinutes;
          }

          const { billable, nonBillable, idle } = classifyTimesheetItemMinutes(
            item,
            activityMaster,
          );
          totalBillableMinutes += billable;
          totalNonBillableMinutes += nonBillable;
          totalIdleMinutes += idle;
        }

        const billedType = item.billed_type || item.billing_type || item.billingType || "";
        const billedNorm = String(billedType).trim().toLowerCase().replace(/[-\s_]/g, "");
        const isNonBillable = billedNorm === "nonbillable";
        const isLeave = billedNorm === "leave";

        exportData.push({
          _sortYmd:
            formatDateOnlyYmd(item.date) ||
            formatDateOnlyYmd(timesheet.start_date),
          Date: formatDateOnlyDisplay(item.date),
          Employee: timesheet.employee_name,
          "Start Date": formatDateOnlyDisplay(timesheet.start_date),
          "End Date": formatDateOnlyDisplay(timesheet.end_date),
          "Billing Type": billedType,
          Client: isNonBillable || isLeave ? "" : formatClientForImportRow(item),
          "Business Name":
            isNonBillable || isLeave
              ? ""
              : String(item?.business_name || "").trim(),
          Activity: item.activity || "Others",
          "Sub Activity": item.sub_activity || item.custom_sub_activity || "",
          Description: item.description || "",
          Hours: formatMinutesAsHMM(itemMinutes),
          Status: getTimesheetStatusLabel(timesheet.status),
          "Last Update": dateTimeFormatter(timesheet.updatedAt),
        });
      });
    }

    exportData.sort((a, b) => compareDateOnly(a._sortYmd, b._sortYmd));
    exportData.forEach((row) => {
      delete row._sortYmd;
    });

    if (!exportData.length) {
      Toaster.warning("No timesheet items within the selected date range");
      return;
    }

    const headers = [
      "Date",
      "Employee",
      "Start Date",
      "End Date",
      "Billing Type",
      "Client",
      "Business Name",
      "Activity",
      "Sub Activity",
      "Description",
      "Hours",
      "Status",
      "Last Update",
    ];

    // 1️⃣ Force headers (THIS FIXES EMPTY EXCEL)
    const ws = utils.aoa_to_sheet([headers]);
    // ✅ Make Excel headers bold
    headers.forEach((_, index) => {
      const cellAddress = utils.encode_cell({ r: 0, c: index });
      if (!ws[cellAddress]) return;

      ws[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "E6F0FA" } },
      };
    });
    // 2️⃣ Summary rows at the end
    exportData.push({
      Date: "",
      Employee: "",
      "Start Date": "",
      "End Date": "",
      "Billing Type": "",
      Client: "",
      "Business Name": "",
      Activity: "",
      "Sub Activity": "",
      Description: "Total Approved Hours",
      Hours: formatMinutesAsHMM(totalApprovedMinutes),
      Status: "",
      "Last Update": "",
    });
    exportData.push({
      Date: "",
      Employee: "",
      "Start Date": "",
      "End Date": "",
      "Billing Type": "",
      Client: "",
      "Business Name": "",
      Activity: "",
      "Sub Activity": "",
      Description: "Total Pending Hours",
      Hours: formatMinutesAsHMM(totalPendingMinutes),
      Status: "",
      "Last Update": "",
    });
    exportData.push({
      Date: "",
      Employee: "",
      "Start Date": "",
      "End Date": "",
      "Billing Type": "",
      Client: "",
      "Business Name": "",
      Activity: "",
      "Sub Activity": "",
      Description: "Total Billable Hours",
      Hours: formatMinutesAsHMM(totalBillableMinutes),
      Status: "",
      "Last Update": "",
    });
    exportData.push({
      Date: "",
      Employee: "",
      "Start Date": "",
      "End Date": "",
      "Billing Type": "",
      Client: "",
      "Business Name": "",
      Activity: "",
      "Sub Activity": "",
      Description: "Total Non-Billable Hours",
      Hours: formatMinutesAsHMM(totalNonBillableMinutes),
      Status: "",
      "Last Update": "",
    });
    exportData.push({
      Date: "",
      Employee: "",
      "Start Date": "",
      "End Date": "",
      "Billing Type": "",
      Client: "",
      "Business Name": "",
      Activity: "",
      "Sub Activity": "",
      Description: "Total Idle Hours",
      Hours: formatMinutesAsHMM(totalIdleMinutes),
      Status: "",
      "Last Update": "",
    });
    const leaveDays =
      totalLeaveMinutes > 0
        ? `${formatMinutesAsHMM(totalLeaveMinutes)} (${(totalLeaveMinutes / (8 * 60)).toLocaleString("en-US", { maximumFractionDigits: 1 })} day${totalLeaveMinutes === 8 * 60 ? "" : "s"})`
        : formatMinutesAsHMM(0);
    exportData.push({
      Date: "",
      Employee: "",
      "Start Date": "",
      "End Date": "",
      "Billing Type": "",
      Client: "",
      "Business Name": "",
      Activity: "",
      "Sub Activity": "",
      Description: "Total Leave",
      Hours: leaveDays,
      Status: "",
      "Last Update": "",
    });

    // 3️⃣ Append data rows
    utils.sheet_add_json(ws, exportData, {
      origin: "A2",
      skipHeader: true,
    });

    // Make totals/summary rows bold
    const totalRowsCount = 6;
    const L = exportData.length;
    for (let offset = 0; offset < totalRowsCount; offset++) {
      const rowIndex = L - totalRowsCount + 1 + offset;
      headers.forEach((_, colIndex) => {
        const cellAddress = utils.encode_cell({ r: rowIndex, c: colIndex });
        if (!ws[cellAddress]) return;
        ws[cellAddress].s = {
          font: { bold: true },
        };
      });
    }

    const wb = {
      Sheets: { Timesheet: ws },
      SheetNames: ["Timesheet"],
    };

    const excelBuffer = write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true, // ✅ REQUIRED for bold styles
    });

    const uniqueEmployees = new Set(
      exportData.map((row) => row.Employee).filter(Boolean),
    );
    const exportLabel =
      uniqueEmployees.size > 1
        ? "Team"
        : String([...uniqueEmployees][0] || "Export").replace(
            /[^a-zA-Z0-9]/g,
            "_",
          );

    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Timesheet_${exportLabel}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    Toaster.success("Detailed Excel exported successfully");
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (getExportableTimesheets.length === 0) {
      Toaster.warning("No approved or pending timesheets to export");
      setExportMenuAnchor(null);
      return;
    }

    // Helper function to load image and convert to base64
    const loadImageAsBase64 = (imagePath) => {
      return new Promise((resolve, reject) => {
        // If imagePath is already a data URL, return it
        if (typeof imagePath === "string" && imagePath.startsWith("data:")) {
          resolve(imagePath);
          return;
        }

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          try {
            const base64 = canvas.toDataURL("image/png");
            resolve(base64);
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = (error) => {
          console.error("Error loading image:", error);
          reject(error);
        };
        img.src = imagePath;
      });
    };

    try {
      // Load logo image from public folder (same as invoice functionality)
      let logoBase64 = null;
      try {
        logoBase64 = await loadImageAsBase64("/resources/images/Finlotax.png");
      } catch (logoLoadError) {
        console.warn(
          "Logo failed to load, will use text fallback:",
          logoLoadError
        );
        // Continue without logo, will use text fallback later
      }

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;
      const rowHeight = 10;
      const headerHeight = 10;

      // Color scheme matching the image
      const headerBgColor = [230, 240, 250]; // Light blue for table header
      const headerTextColor = [60, 60, 60]; // Dark gray for header text
      const textColor = [60, 60, 60]; // Dark gray for body text
      const borderColor = [220, 220, 220]; // Light gray for borders
      const logoColor = [28, 116, 188]; // Blue for FINLOTAX logo (fallback)

      // Collect detailed items for PDF (same as Excel export)
      let pdfItems = [];
      let totalApprovedMinutes = 0;
      let totalPendingMinutes = 0;
      let totalBillableMinutes = 0;
      let totalNonBillableMinutes = 0;
      let totalIdleMinutes = 0;
      let totalLeaveMinutesPdf = 0;

      for (const ts of getExportableTimesheets) {
        const res = await TIMESHEET_ACTION.getTimesheetEntries({
          timesheet_id: ts._rawData.timesheet_id,
        });

        if (!res || !res.length) continue;

        const timesheet = res[0];
        if (!timesheet.itemDetails || !timesheet.itemDetails.length) continue;

        const statusCode = getTimesheetStatusCode(timesheet.status);

        timesheet.itemDetails.forEach((item) => {
          if (!isExportItemInFilterRange(item.date)) return;

          const itemMinutes = parseItemHoursToMinutes(item.hours);
          if (statusCode === 2) {
            totalApprovedMinutes += itemMinutes;
          } else if (statusCode === 1) {
            totalPendingMinutes += itemMinutes;
          }

          const { billable, nonBillable, idle } = classifyTimesheetItemMinutes(
            item,
            activityMaster,
          );
          const isPdfLeaveItem =
            String(item?.billed_type || item?.billing_type || item?.billingType || "")
              .trim()
              .toLowerCase()
              .replace(/[-\s_]/g, "") === "leave";
          if (isPdfLeaveItem) totalLeaveMinutesPdf += itemMinutes;
          totalBillableMinutes += billable;
          totalNonBillableMinutes += nonBillable;
          totalIdleMinutes += idle;

          pdfItems.push({
            _sortDate: new Date(item.date),
            start_date: timesheet.start_date,
            end_date: timesheet.end_date,
            employee_name: timesheet.employee_name,
            date: item.date,
            activity: item.activity || "Others",
            sub_activity: item.sub_activity || item.custom_sub_activity || "",
            description: item.description || "",
            hours: formatMinutesAsHMM(itemMinutes),
            status: timesheet.status,
            statusLabel: getTimesheetStatusLabel(timesheet.status),
            updatedAt: timesheet.updatedAt,
          });
        });
      }
      pdfItems.sort((a, b) => a._sortDate - b._sortDate);

      if (!pdfItems.length) {
        Toaster.warning("No timesheet items within the selected date range");
        setExportMenuAnchor(null);
        return;
      }

      const totalApprovedHoursFormatted = formatMinutesAsHMM(totalApprovedMinutes);
      const totalPendingHoursFormatted = formatMinutesAsHMM(totalPendingMinutes);
      const totalBillableHoursFormatted = formatMinutesAsHMM(totalBillableMinutes);
      const totalNonBillableHoursFormatted = formatMinutesAsHMM(
        totalNonBillableMinutes,
      );
      const totalIdleHoursFormatted = formatMinutesAsHMM(totalIdleMinutes);
      const totalLeaveDaysPdf =
        totalLeaveMinutesPdf > 0
          ? `${formatMinutesAsHMM(totalLeaveMinutesPdf)} (${(totalLeaveMinutesPdf / (8 * 60)).toLocaleString("en-US", { maximumFractionDigits: 1 })} day${totalLeaveMinutesPdf === 8 * 60 ? "" : "s"})`
          : formatMinutesAsHMM(0);

      const uniquePdfEmployees = new Set(
        pdfItems.map((item) => item.employee_name).filter(Boolean),
      );
      const employeeName =
        uniquePdfEmployees.size > 1
          ? "Team"
          : pdfItems[0]?.employee_name ||
            getExportableTimesheets[0]?.employee_name ||
            "Employee";

      // Format date helper
      const formatDateForPDF = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${month} ${day.toString().padStart(2, "0")} ${year}`;
      };

      // Format date time for Last Update
      const formatDateTimeForPDF = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${month} ${day
          .toString()
          .padStart(2, "0")} ${year}, ${hours}:${minutes}`;
      };

      // FINLOTAX Logo (top left) - using image instead of text
      if (logoBase64) {
        try {
          const logoWidth = 50; // Logo width
          const logoHeight = 15; // Logo height (maintains aspect ratio)
          doc.addImage(
            logoBase64,
            "PNG",
            margin,
            yPosition - 8,
            logoWidth,
            logoHeight
          );
        } catch (logoError) {
          // Fallback to text if logo fails to add
          console.warn(
            "Logo failed to add to PDF, using text fallback:",
            logoError
          );
          doc.setTextColor(...logoColor);
          doc.setFontSize(18);
          doc.setFont(undefined, "bold");
          doc.text("FINLOTAX", margin, yPosition - 6);
        }
      } else {
        // Fallback to text if logo failed to load
        doc.setTextColor(...logoColor);
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("FINLOTAX", margin, yPosition - 6);
      }

      yPosition += 12;

      // Employee name and hour summaries
      doc.setFontSize(11);
      doc.setFont(undefined, "normal");
      doc.setTextColor(...textColor);
      doc.text(`Employee: ${employeeName}`, margin, yPosition);

      const approvedSummary = `Total Approved Hours: ${totalApprovedHoursFormatted}`;
      const approvedWidth = doc.getTextWidth(approvedSummary);
      doc.text(approvedSummary, pageWidth - margin - approvedWidth, yPosition);

      yPosition += 7;

      const pendingSummary = `Total Pending Hours: ${totalPendingHoursFormatted}`;
      const pendingWidth = doc.getTextWidth(pendingSummary);
      doc.text(pendingSummary, pageWidth - margin - pendingWidth, yPosition);

      yPosition += 7;

      const billableSummary = `Total Billable Hours: ${totalBillableHoursFormatted}`;
      const billableWidth = doc.getTextWidth(billableSummary);
      doc.text(billableSummary, pageWidth - margin - billableWidth, yPosition);

      yPosition += 7;

      const nonBillableSummary = `Total Non-Billable Hours: ${totalNonBillableHoursFormatted}`;
      const nonBillableWidth = doc.getTextWidth(nonBillableSummary);
      doc.text(
        nonBillableSummary,
        pageWidth - margin - nonBillableWidth,
        yPosition,
      );

      yPosition += 7;

      const idleSummary = `Total Idle Hours: ${totalIdleHoursFormatted}`;
      const idleWidth = doc.getTextWidth(idleSummary);
      doc.text(idleSummary, pageWidth - margin - idleWidth, yPosition);

      yPosition += 7;

      const leaveSummary = `Total Leave: ${totalLeaveDaysPdf}`;
      const leaveWidth = doc.getTextWidth(leaveSummary);
      doc.text(leaveSummary, pageWidth - margin - leaveWidth, yPosition);

      yPosition += 15;

      const headers = [
        "Date",
        "Activity",
        "Sub Activity",
        "Description",
        "Hours",
        "Status",
      ];

      const colWidths = [22, 28, 28, 42, 18, 22];

      const totalWidth = colWidths.reduce((sum, w) => sum + w, 0);
      // Align table to the left margin so none of the columns are cut off
      const startX = margin;

      // Draw header row with light blue background and rounded corners
      const borderRadius = 4;
      doc.setFillColor(...headerBgColor);
      doc.roundedRect(
        startX,
        yPosition,
        totalWidth,
        headerHeight,
        borderRadius,
        borderRadius,
        "F"
      );

      doc.setTextColor(...headerTextColor);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");

      let xPos = startX;
      headers.forEach((header, idx) => {
        doc.text(header, xPos + 5, yPosition + headerHeight / 2 + 3, {
          align: "left",
        });
        xPos += colWidths[idx];
      });

      yPosition += headerHeight;

      // Draw data rows (detailed items)
      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
      doc.setTextColor(...textColor);

      pdfItems.forEach((item) => {
        const rowData = [
          item.date ? dateFormatter(item.date) : "---",
          String(item.activity || "Others"),
          String(item.sub_activity || item.custom_sub_activity || ""),
          String(item.description || ""),
          String(item.hours || "---"),
          String(item.statusLabel || getTimesheetStatusLabel(item.status)),
        ];

        // Prepare wrapped text for each cell and compute dynamic row height
        const cellLines = rowData.map((cell, idx) =>
          doc.splitTextToSize(String(cell || ""), colWidths[idx] - 10)
        );
        const maxLines = cellLines.reduce(
          (max, lines) => Math.max(max, (lines && lines.length) || 1),
          1
        );
        const lineHeight = 4; // vertical spacing between lines
        const rowHeightDynamic = Math.max(rowHeight, maxLines * lineHeight + 4);

        // Check if new page is needed
        if (yPosition + rowHeightDynamic > pageHeight - 20) {
          doc.addPage();
          yPosition = margin;

          // Redraw header on new page with rounded corners
          doc.setFillColor(...headerBgColor);
          doc.roundedRect(
            startX,
            yPosition,
            totalWidth,
            headerHeight,
            borderRadius,
            borderRadius,
            "F"
          );
          doc.setTextColor(...headerTextColor);
          doc.setFont(undefined, "bold");
          xPos = startX;
          headers.forEach((header, idx) => {
            doc.text(header, xPos + 5, yPosition + headerHeight / 2 + 3, {
              align: "left",
            });
            xPos += colWidths[idx];
          });
          yPosition += headerHeight;
        }

        // Draw cell borders and text
        xPos = startX;
        rowData.forEach((cell, idx) => {
          // Draw vertical line between columns (except for first column)
          if (idx > 0) {
            doc.setDrawColor(...borderColor);
            doc.setLineWidth(0.1);
            doc.line(xPos, yPosition, xPos, yPosition + rowHeightDynamic);
          }

          // Cell text (dark gray, left-aligned)
          doc.setTextColor(...textColor);
          const lines =
            cellLines[idx] && cellLines[idx].length
              ? cellLines[idx]
              : [String(cell || "")];
          lines.forEach((line, lineIndex) => {
            const textY = yPosition + 5 + lineIndex * lineHeight;
            doc.text(line, xPos + 5, textY, {
              align: "left",
              maxWidth: colWidths[idx] - 10,
            });
          });

          xPos += colWidths[idx];
        });

        // Draw horizontal line at bottom of row
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.1);
        doc.line(
          startX,
          yPosition + rowHeightDynamic,
          startX + totalWidth,
          yPosition + rowHeightDynamic
        );

        yPosition += rowHeightDynamic;
      });

      // Draw left and right borders for the entire table
      const tableStartY =
        yPosition - headerHeight - pdfItems.length * rowHeight;
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.1);
      doc.line(startX, tableStartY, startX, yPosition); // Left border
      doc.line(
        startX + totalWidth,
        tableStartY,
        startX + totalWidth,
        yPosition
      ); // Right border

      // Draw rounded rectangle outline for the entire table (with rounded top corners from header)
      // The header already has rounded top corners, so we just need to complete the border
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.1);
      // Top border (already drawn by header, but ensure it's visible)
      doc.line(startX, tableStartY, startX + totalWidth, tableStartY);

      // Save PDF
      const employeeNameForPDF = employeeName.replace(/[^a-zA-Z0-9]/g, "_");
      const currentDateStr = new Date().toISOString().split("T")[0];
      const fileName = `Timesheet_${employeeNameForPDF}_${currentDateStr}.pdf`;
      doc.save(fileName);
      setExportMenuAnchor(null);
      Toaster.success("PDF file exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      Toaster.error("Error occurred while exporting PDF");
      setExportMenuAnchor(null);
    }
  };

  // SubActivity Component
  const SubActivity = ({ onClose }) => {
    const {
      master: { activity, getSubActivityDetails },
    } = useSelector((state) => state);
    const [activityFilter, setActivityFilter] = useState("");

    const activityOptions = useMemo(() => {
      if (!activity || !Array.isArray(activity)) return [];
      return activity.map((act) => {
        const normalized = normalizeActivityOption(act);
        const id = normalized.id ?? normalized.activity_id;
        const value =
          normalized.value ||
          normalized.activity ||
          normalized.name ||
          (id != null ? String(id) : "");
        return { ...normalized, id, value };
      });
    }, [activity]);

    const onChange = (value, key) => {
      checkValidation({
        key,
        value,
        state: activityData,
        setState: setActivityData,
      });
    };

    const defaultValuesActivity = {
      activity_id: {
        value: "",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
      },
      sub_activity: {
        value: "",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
      },
    };
    const [activityData, setActivityData] = useState(defaultValuesActivity);

    const onSubActivitySubmit = async () => {
      const validate = onSubmitValidation({
        state: activityData,
        setState: setActivityData,
        checkFields: ["activity_id", "sub_activity"],
      });
      if (!validate.length) {
        const signal = await MASTER_ACTION.insertSubActivity({
          activity_id: activityData.activity_id.value,
          sub_activity: activityData.sub_activity.value,
        });
        if (signal.status) {
          dispatch(MASTER_ACTION.getSubActivityDetails());
          Toaster.success(signal.message);
          setActivityData((prev) => ({ ...prev, sub_activity: "" }));
        } else Toaster.error(signal.message || "Sub Activity not got inserted");
      }
    };

    const onAction = async (type, actionData, actionIndex) => {
      if (type === "delete") {
        onDeleteSubActivity(actionData.sub_activity_id);
      }
    };
    const onDeleteSubActivity = async (sub_activity_id) => {
      const signal = await MASTER_ACTION.deleteSubActivity(sub_activity_id);
      if (signal.status) {
        dispatch(MASTER_ACTION.getSubActivityDetails());
        Toaster.success(signal.message);
      } else Toaster.error(signal.message || "Sub Activity not deleted");
    };
    const subActivityTable = useMemo(() => {
      const filtered = activityFilter
        ? (getSubActivityDetails || []).filter(
            (r) => String(r.activity_id) === String(activityFilter),
          )
        : getSubActivityDetails;

      return (filtered || []).map(
        ({ activity, sub_activity, updatedAt, sub_activity_id, activity_id }, index) => {
          return {
            s_no: index + 1,
            activity: activity,
            activity_id,
            subactivity: sub_activity,
            updated: dateTimeFormatter(updatedAt),
            enabledActions: { delete: true },
            sub_activity_id: sub_activity_id,
          };
        }
      );
    }, [getSubActivityDetails, activityFilter]);

    return (
      <div className="subactivity-modal-wrapper">
        <div className="subactivity-modal-header">
          <h2 className="subactivity-modal-title">Sub activity</h2>
          <IconButton
            className="subactivity-close-btn"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="subactivity-form-row">
          <div className="subactivity-form-field">
            <Labelbox
              type="select"
              labelname="Filter Activity"
              dropdown={[
                { id: "", value: "All" },
                ...activityOptions,
              ]}
              changeData={(e) => setActivityFilter(e)}
              value={activityFilter}
            />
          </div>
          <div className="subactivity-form-field">
            <Labelbox
              type="select"
              labelname="Activity"
              dropdown={activityOptions}
              changeData={(e) => onChange(e, "activity_id")}
              value={activityData.activity_id.value}
              error={activityData.activity_id.error}
              errmsg={activityData.activity_id.errmsg}
            />
          </div>
          <div className="subactivity-form-field">
            <Labelbox
              type="text"
              labelname="Sub Activity"
              changeData={(e) => onChange(e, "sub_activity")}
              value={activityData.sub_activity.value}
              error={activityData.sub_activity.error}
              errmsg={activityData.sub_activity.errmsg}
            />
          </div>
          <div className="subactivity-submit-btn">
            <Button text="Submit" large handleClick={onSubActivitySubmit} />
          </div>
        </div>
        <div className="subactivity-table-container">
          <Table
            variant="small"
            title=""
            header={[
              { id: "s_no", label: "SI No." },
              { id: "activity", label: "Activity" },
              { id: "subactivity", label: "Subactivity" },
              { id: "updated", label: "Last Updated" },
              { id: "action", label: "Action" },
            ]}
            data={subActivityTable}
            actions={["delete"]}
            onActionClick={onAction}
            hiddenFields={["sub_activity_id", "activity_id"]}
            aligncss="subactivity-table"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="timesheet-dashboard-wrapper">
      {/* Header Section */}
      <div className="timesheet-dashboard-header">
        <h1 className="timesheet-dashboard-title"></h1>
        <div className="timesheet-dashboard-actions">
          {(isAdmin || isManager || isEmployee) && (
            <Button
              variant="outline"
              // iconCls="btn_icon"
              icon={<AddCircleOutlineOutlined />}
              text="Add Timesheet"
              handleClick={() => navigate(getRolePath("/create_timesheet"))}
              className="timesheet-dashboard-add-btn"
            />
          )}
          {isAdmin && (
            <Button
              variant="outline"
              // iconCls="btn_icon"
              icon={<AddCircleOutlineOutlined />}
              text="Add Sub Activity"
              handleClick={() => {
                setSubActivityState((prev) => ({ ...prev, open: true }));
              }}
              className="timesheet-dashboard-add-btn"
            />
          )}
        </div>
      </div>

      {/* Search Timesheet Card */}
      <div className="timesheet-search-card panel">
        <div className="panel-heading">
          <h2 className="panel-title">Search Timesheet</h2>
        </div>
        <div className="panel-body">
          <div className="timesheet-search-fields reports-date-range">
            <div className="timesheet-search-field">
              <Labelbox
                type="select"
                labelname="Date Range"
                value={dateRangePreset}
                dropdown={REPORT_DATE_RANGE_OPTIONS.map((range) => ({
                  id: range,
                  value: range,
                }))}
                changeData={(e) => handleDateRangePresetChange(e)}
              />
            </div>
            <div className="timesheet-search-field">
              <Labelbox
                type="datepicker"
                labelname="Start Date"
                changeData={(e) => onChange(e, "start_date")}
                value={fieldConfiguration.start_date.value}
                error={fieldConfiguration.start_date.error}
                errmsg={fieldConfiguration.start_date.errmsg}
              />
            </div>
            <div className="timesheet-search-field">
              <Labelbox
                type="datepicker"
                labelname="End Date"
                changeData={(e) => onChange(e, "end_date")}
                value={fieldConfiguration.end_date.value}
                error={fieldConfiguration.end_date.error}
                errmsg={fieldConfiguration.end_date.errmsg}
              />
            </div>
            {(isTimesheetAdminViewer) && (
              <div className="timesheet-search-field">
                <Labelbox
                  type="select"
                  labelname="Employee"
                  changeData={(e) => onChange(e, "employee_id")}
                  value={fieldConfiguration.employee_id.value}
                  dropdown={employeesData}
                  error={fieldConfiguration.employee_id.error}
                  errmsg={fieldConfiguration.employee_id.errmsg}
                />
              </div>
            )}
            {(isManager || isAdmin) && (
              <div
                className="timesheet-search-field"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "8px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showManagerOwnTimesheets}
                    onChange={(e) =>
                      setShowManagerOwnTimesheets(e.target.checked)
                    }
                  />
                  <span>Show My Timesheets</span>
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="panel-footer">
          <div className="timesheet-search-actions">
            <button className="employee-btn-cancel" onClick={handleCancel}>
              Reset
            </button>
            <Button
              text="Search"
              large
              handleClick={onSearch}
              className="timesheet-search-btn"
            />
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div
        className="timesheet-table-card panel"
        style={{ position: "relative" }}
      >
        {/* Export — admin/manager detailed report; employee import-template Excel */}
        {(canExportTimesheets || canExportEmployeeImportExcel) && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "0px 0px 16px 0px",
              // borderBottom: "1px solid #e0e0e0",
            }}
          >
            {canExportEmployeeImportExcel ? (
              <Button
                icon={<GetApp />}
                text="Export"
                variant="outline"
                handleClick={handleExportEmployeeImportExcel}
              />
            ) : (
            <div style={{ position: "relative" }} ref={exportButtonRef}>
              <Button
                icon={<GetApp />}
                text="Export"
                variant="outline"
                handleClick={(e) => {
                  e.stopPropagation();
                  setExportMenuAnchor(
                    exportMenuAnchor ? null : exportButtonRef.current
                  );
                }}
              />
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => {
                  setExportMenuAnchor(null);
                }}
                style={{ marginTop: "5px" }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                disableAutoFocusItem
              >
                <MenuItem
                  onClick={() => {
                    handleExportExcel();
                    setExportMenuAnchor(null);
                  }}
                >
                  <TableChart
                    style={{ marginRight: "8px", fontSize: "18px" }}
                  />
                  Export to Excel
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleExportPDF();
                    setExportMenuAnchor(null);
                  }}
                >
                  <PictureAsPdf
                    style={{ marginRight: "8px", fontSize: "18px" }}
                  />
                  Export to PDF
                </MenuItem>
              </Menu>
            </div>
            )}
          </div>
        )}
        <Table
          variant="small"
          title=""
          header={isTimesheetAdminViewer ? header1 : header}
          data={timesheetTableData}
          hiddenFields={
            !isTimesheetAdminViewer
              ? [
                  "timesheet_id",
                  "employee_name",
                  "employee_id",
                  "employee_id_original",
                  "total_hours",
                  "updatedAt",
                  "status_code",
                  "enabledActions",
                  "_rawData",
                ]
              : [
                  "timesheet_id",
                  "employee_name",
                  "employee_id_original",
                  "total_hours",
                  "updatedAt",
                  "status_code",
                  "enabledActions",
                  "_rawData",
                ]
          }
          aligncss="dashboard-table"
          onRowClick={(row) => {
            handleViewTimesheetForRow(row);
          }}
        />
      </div>
      {deleteTimesheetDialog.open && (
        <Modal
          title={null}
          open={deleteTimesheetDialog.open}
          handleClose={() =>
            setDeleteTimesheetDialog({ open: false, timesheetData: null })
          }
          className="delete-confirm-modal timesheet-view-modal"
        >
          <div className="delete-confirm">
            <div className="delete-confirm-header">
              <h3>Delete Timesheet</h3>
              <button
                type="button"
                className="delete-confirm-close"
                onClick={() =>
                  setDeleteTimesheetDialog({ open: false, timesheetData: null })
                }
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <p className="delete-confirm-text">
              Are you sure you want to delete this timesheet?
            </p>

            <div className="delete-confirm-actions">
              <button
                type="button"
                className="delete-btn secondary"
                onClick={() =>
                  setDeleteTimesheetDialog({ open: false, timesheetData: null })
                }
              >
                No
              </button>
              <button
                type="button"
                className="delete-btn danger"
                onClick={confirmDeleteTimesheet}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      <Modal className="timesheet-view-modal" open={openModal}>
        <Grid
          container
          xs={12}
          sm={12}
          md={12}
          lg={12}
          // spacing={3}
          style={{ margin: 0 }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 style={{ fontWeight: "bold" }}>Rejection Reason</h2>
            <button
              className="close-btn"
              onClick={() => {
                setOpenModal(false);
                fieldConfiguration.rejected_comments.value = "";
              }}
            >
              ✕
            </button>
          </div>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            style={{ marginBottom: 10, marginTop: 10 }}
          >
            <Labelbox
              type="textarea"
              labelname="Comments"
              changeData={(e) => onChange(e, "rejected_comments")}
              value={fieldConfiguration.rejected_comments.value}
              error={fieldConfiguration.rejected_comments.error}
              errmsg={fieldConfiguration.rejected_comments.errmsg}
              rows={3}
            />
          </Grid>
          <div
            className="modal-actions-right"
            style={{ justifyContent: "flex-end" }}
          >
            <button
              onClick={() => {
                onRejectSubmit();
                rejectedEmployeeId.current = null;
                setOpenModal(false);
              }}
              className="upload-button"
              text="Submit"
            >
              Submit
            </button>
          </div>
        </Grid>
      </Modal>
      <Modal
        title={null}
        maxWidth
        open={subActivityState.open}
        handleClose={() => {
          setSubActivityState({ open: false });
        }}
        className="subactivity-modal"
      >
        <SubActivity
          onClose={() => {
            setSubActivityState({ open: false });
          }}
        />
      </Modal>
      {viewMode.state && viewMode.data instanceof Object && (
        <Modal
          title={null}
          maxWidth
          open={viewMode.state}
          handleClose={() => {
            setViewMode({ state: false, date: [] });
          }}
          className="timesheet-view-modal"
        >
          <TimeSheetView
            data={viewMode.data}
            onClose={() => {
              setViewMode({ state: false, date: [] });
            }}
          />
        </Modal>
      )}
      <EditTimesheetModal
        open={editModalOpen}
        handleClose={() => {
          setEditModalOpen(false);
          setEditTimesheetId(null);
        }}
        timesheetId={editTimesheetId}
      />
    </div>
  );
};
export default Dashboard;